<script>
import { mapActions, mapGetters } from 'vuex';
import PageHeader from 'dashboard/components/dahreply/PageHeader.vue';
import PaginationBar from 'dashboard/components/dahreply/PaginationBar.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import CondoModal from './components/CondoModal.vue';
import CondoDocuments from './components/CondoDocuments.vue';
import { apiErrorMessage } from '../../../api/coLiving/errors';
import { CARD_CLASS } from './constants';
import { condoHue } from './condoForm';
import { DOC_TYPE_BY_TAB } from './documents';

const DOC_TABS = ['notice', 'mom', 'rules'];
const PAGE_SIZE_OPTIONS = [10, 20, 50];

// Condo Details. The header and the Resident Profile read the condo and its
// tenants; Notice, MOM and House Rules are the condo's own files (see
// CondoDocuments), each optionally shared with the tenant chat.
export default {
  name: 'CoLivingCondoDetailsPage',
  components: {
    PageHeader,
    PaginationBar,
    LoadingState,
    CondoModal,
    CondoDocuments,
  },
  data() {
    return {
      cardClass: CARD_CLASS,
      docTabs: DOC_TABS,
      pageSizeOptions: PAGE_SIZE_OPTIONS,
      tab: 'residents',
      condo: null,
      page: 1,
      pageSize: PAGE_SIZE_OPTIONS[0],
      isLoading: false,
      loadError: '',
      isModalOpen: false,
    };
  },
  computed: {
    ...mapGetters('coLivingResidents', {
      tenants: 'getTenants',
      meta: 'getTenantMeta',
      uiFlags: 'getUiFlags',
    }),
    ...mapGetters('coLivingConfig', { settings: 'getSettings' }),
    condoId() {
      return this.$route.params.condoId;
    },
    docType() {
      return DOC_TYPE_BY_TAB[this.tab] || '';
    },
    timezone() {
      return (this.settings && this.settings.timezone) || '';
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.meta.total / this.pageSize));
    },
    pagination() {
      return {
        currentPage: this.page,
        totalPages: this.totalPages,
        pageSize: this.pageSize,
      };
    },
    heroStyle() {
      const hue = condoHue(this.condoId);
      return {
        background: `linear-gradient(135deg, hsl(${hue} 62% 90%), hsl(${
          (hue + 45) % 360
        } 60% 78%))`,
      };
    },
  },
  mounted() {
    // The tenants list is shared with the Residents page: start empty.
    this.clearTenants();
    this.fetchSettings();
    this.loadCondo();
    this.loadTenants();
  },
  methods: {
    ...mapActions('coLivingCondos', ['fetchCondo']),
    ...mapActions('coLivingConfig', ['fetchSettings']),
    ...mapActions('coLivingResidents', ['fetchTenants', 'clearTenants']),
    async loadCondo() {
      this.isLoading = true;
      this.loadError = '';
      try {
        this.condo = await this.fetchCondo(this.condoId);
      } catch (err) {
        this.loadError = apiErrorMessage(
          this,
          err,
          'CO_LIVING.COMMON.LOAD_FAILED'
        );
      } finally {
        this.isLoading = false;
      }
    },
    async loadTenants() {
      try {
        await this.fetchTenants({
          condo_id: this.condoId,
          page: this.page,
          page_size: this.pageSize,
        });
      } catch (err) {
        this.loadError = apiErrorMessage(
          this,
          err,
          'CO_LIVING.COMMON.LOAD_FAILED'
        );
      }
    },
    goToPage(page) {
      this.page = page;
      this.loadTenants();
    },
    onPageSizeChange(size) {
      this.pageSize = size;
      this.page = 1;
      this.loadTenants();
    },
    backToCondos() {
      this.$router.push({
        name: 'co_living_condos',
        params: { accountId: this.$route.params.accountId },
      });
    },
    manageResidents() {
      this.$router.push({
        name: 'co_living_residents',
        params: { accountId: this.$route.params.accountId },
        query: { condo_id: this.condoId },
      });
    },
  },
};
</script>

<template>
  <div class="flex-1 overflow-auto p-6" data-testid="co-living-condo-details">
    <PageHeader
      :title="$t('CO_LIVING.CONDOS.DETAILS_TITLE')"
      icon="condo"
      allow-back
      :back-url="{
        name: 'co_living_condos',
        params: { accountId: $route.params.accountId },
      }"
    />

    <LoadingState
      v-if="isLoading && !condo"
      :message="$t('CO_LIVING.COMMON.LOADING')"
    />
    <p
      v-else-if="loadError && !condo"
      class="text-sm text-red-500"
      data-testid="co-living-load-error"
    >
      {{ loadError }}
    </p>

    <div v-else-if="condo" class="flex flex-col gap-6">
      <article class="flex flex-col md:flex-row gap-6" :class="[cardClass]">
        <div
          class="w-full md:w-56 h-32 rounded-lg shrink-0"
          :style="heroStyle"
          aria-hidden="true"
        />
        <div class="flex flex-col gap-3 flex-1">
          <div>
            <h2
              class="text-lg font-medium text-slate-900 dark:text-slate-100"
              data-testid="co-living-condo-title"
            >
              {{ condo.name }}
              <span
                v-if="!condo.active"
                class="ml-2 px-2 py-0.5 text-xs rounded bg-slate-100 text-slate-600 dark:bg-slate-600 dark:text-slate-300"
              >
                {{ $t('CO_LIVING.COMMON.ARCHIVED') }}
              </span>
            </h2>
            <p class="text-sm text-slate-600 dark:text-slate-300">
              {{ condo.address }}
              <span
                v-if="!condo.located"
                class="ml-2 text-xs text-amber-600 dark:text-amber-400"
              >
                {{ $t('CO_LIVING.CONDOS.NOT_LOCATED') }}
              </span>
            </p>
          </div>
          <p class="text-sm text-slate-600 dark:text-slate-300">
            {{ condo.description || '-' }}
          </p>
          <dl class="flex gap-6 text-sm">
            <div>
              <dt class="text-slate-500">
                {{ $t('CO_LIVING.CONDOS.COLUMNS.UNITS') }}
              </dt>
              <dd class="text-slate-900 dark:text-slate-100">
                {{ condo.unit_count }}
              </dd>
            </div>
            <div>
              <dt class="text-slate-500">
                {{ $t('CO_LIVING.CONDOS.COLUMNS.TENANTS') }}
              </dt>
              <dd class="text-slate-900 dark:text-slate-100">
                {{ condo.tenant_count }}
              </dd>
            </div>
            <div>
              <dt class="text-slate-500">
                {{ $t('CO_LIVING.CONDOS.COLUMNS.OPEN') }}
              </dt>
              <dd class="text-slate-900 dark:text-slate-100">
                {{ condo.open_ticket_count }}
              </dd>
            </div>
          </dl>
          <div>
            <woot-button
              variant="clear"
              size="small"
              icon="edit"
              data-testid="co-living-condo-edit"
              @click="isModalOpen = true"
            >
              {{ $t('CO_LIVING.CONDOS.EDIT_CONDO') }}
            </woot-button>
          </div>
        </div>
      </article>

      <nav class="flex gap-4 border-b border-slate-200 dark:border-slate-700">
        <button
          v-for="doc in docTabs"
          :key="doc"
          type="button"
          class="pb-2 text-sm font-medium border-b-2"
          :class="
            tab === doc
              ? 'border-woot-500 text-woot-600 dark:text-woot-400'
              : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100 hover:border-slate-300 dark:hover:border-slate-500 transition-colors'
          "
          :data-testid="`co-living-condo-tab-${doc}`"
          @click="tab = doc"
        >
          {{ $t(`CO_LIVING.CONDOS.TABS.${doc.toUpperCase()}`) }}
        </button>
        <button
          type="button"
          class="pb-2 text-sm font-medium border-b-2"
          :class="
            tab === 'residents'
              ? 'border-woot-500 text-woot-600 dark:text-woot-400'
              : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100 hover:border-slate-300 dark:hover:border-slate-500 transition-colors'
          "
          data-testid="co-living-condo-tab-residents"
          @click="tab = 'residents'"
        >
          {{ $t('CO_LIVING.CONDOS.TABS.RESIDENTS') }}
        </button>
      </nav>

      <CondoDocuments
        v-if="tab !== 'residents'"
        :condo-id="condoId"
        :type="docType"
        :timezone="timezone"
      />

      <section v-else data-testid="co-living-condo-residents">
        <div class="flex items-start justify-between gap-4 mb-3">
          <p class="text-sm text-slate-600 dark:text-slate-300">
            {{ $t('CO_LIVING.CONDOS.RESIDENTS_NOTE') }}
          </p>
          <woot-button
            color-scheme="secondary"
            data-testid="co-living-condo-manage-residents"
            @click="manageResidents"
          >
            {{ $t('CO_LIVING.CONDOS.MANAGE_RESIDENTS') }}
          </woot-button>
        </div>
        <p
          v-if="!tenants.length && !uiFlags.isFetchingTenants"
          class="text-sm text-slate-500 dark:text-slate-400"
          data-testid="co-living-condo-no-residents"
        >
          {{ $t('CO_LIVING.CONDOS.NO_RESIDENTS') }}
        </p>
        <template v-else>
          <div
            class="overflow-x-auto bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg"
          >
            <table class="w-full text-sm" data-testid="co-living-condo-tenants">
              <thead>
                <tr class="text-left text-slate-500 dark:text-slate-400">
                  <th class="p-3">
                    {{ $t('CO_LIVING.RESIDENTS.COLUMNS.TENANT') }}
                  </th>
                  <th class="p-3">
                    {{ $t('CO_LIVING.RESIDENTS.COLUMNS.UNIT') }}
                  </th>
                  <th class="p-3">
                    {{ $t('CO_LIVING.RESIDENTS.COLUMNS.ROOM') }}
                  </th>
                  <th class="p-3">
                    {{ $t('CO_LIVING.RESIDENTS.COLUMNS.LANGUAGE') }}
                  </th>
                  <th class="p-3">{{ $t('CO_LIVING.COMMON.STATUS') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="tenant in tenants"
                  :key="tenant.id"
                  class="border-t border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100"
                >
                  <td class="p-3">
                    {{ tenant.name }}
                    <span class="block text-xs text-slate-500">
                      {{ tenant.phone }}
                    </span>
                  </td>
                  <td class="p-3">{{ tenant.unit_name }}</td>
                  <td class="p-3">{{ tenant.room_name || '-' }}</td>
                  <td class="p-3">{{ tenant.language || '-' }}</td>
                  <td class="p-3">
                    {{
                      $t(
                        `CO_LIVING.RESIDENTS.STATUS.${tenant.status.toUpperCase()}`
                      )
                    }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <PaginationBar
            :pagination="pagination"
            :page-size-options="pageSizeOptions"
            :loading="uiFlags.isFetchingTenants"
            @previous="goToPage(page - 1)"
            @next="goToPage(page + 1)"
            @page-size-change="onPageSizeChange"
          />
        </template>
      </section>
    </div>

    <CondoModal
      v-if="isModalOpen"
      :condo="condo"
      @close="isModalOpen = false"
      @saved="loadCondo"
    />
  </div>
</template>
